<template>
  <div v-if="interpretacion" class="interpretacion-wrapper my-3">
    <!-- Caso: Formulario SRQ (Multidimensional con 4 áreas) -->
    <div v-if="interpretacion.tipo === 'srq'" class="card shadow-sm border-0 rounded overflow-hidden">
      <div class="card-header py-2 px-3 bg-white border-bottom d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <i :class="[interpretacion.icon, interpretacion.textClass, 'mr-2']" style="font-size: 1.1rem;"></i>
          <div>
            <span class="text-uppercase font-weight-bold text-muted d-block" style="font-size: 0.65rem; letter-spacing: 0.5px;">
              Interpretación Cuestionario SRQ-30
            </span>
            <span class="font-weight-bold" :class="interpretacion.textClass" style="font-size: 0.85rem;">
              {{ interpretacion.titulo }}
            </span>
          </div>
        </div>
        <span class="badge px-2 py-1 font-weight-bold" :class="interpretacion.badgeClass" style="font-size: 0.75rem;">
          {{ interpretacion.esPositivo ? 'CASO SOSPECHOSO' : 'SIN SOSPECHA' }}
        </span>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead class="thead-light" style="font-size: 0.72rem;">
              <tr>
                <th class="py-2 pl-3">Área Evaluada</th>
                <th class="py-2 text-center" style="width: 85px;">Puntaje</th>
                <th class="py-2">Criterio e Interpretación</th>
                <th class="py-2 text-center pr-3" style="width: 100px;">Estado</th>
              </tr>
            </thead>
            <tbody style="font-size: 0.78rem;">
              <tr v-for="(item, idx) in interpretacion.detalles" :key="idx" :class="{ 'row-alerta': item.alerta }">
                <td class="align-middle pl-3 font-weight-bold" style="min-width: 160px;">
                  {{ item.nombre }}
                  <small class="text-muted d-block font-weight-normal" style="font-size: 0.7rem;">{{ item.subtitulo }}</small>
                </td>
                <td class="text-center align-middle font-weight-bold">
                  {{ item.puntaje }}
                </td>
                <td class="align-middle" style="line-height: 1.3;">
                  <span :class="item.alerta ? 'text-danger font-weight-bold' : 'text-dark'">
                    {{ item.interpretacion }}
                  </span>
                </td>
                <td class="text-center align-middle pr-3">
                  <span class="badge px-2 py-1 font-weight-bold" :class="item.alerta ? 'badge-danger' : 'badge-success'" style="font-size: 0.7rem;">
                    <i :class="item.alerta ? 'fas fa-exclamation-triangle mr-1' : 'fas fa-check-circle mr-1'"></i>
                    {{ item.alerta ? 'ALERTA' : 'NORMAL' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Caso: Formulario Simple (ej. RQC u otros) -->
    <div
      v-else-if="interpretacion.tipo === 'simple'"
      class="interpretacion-card p-3 rounded shadow-sm d-flex align-items-center justify-content-between"
      :class="interpretacion.cardClass"
    >
      <div class="d-flex align-items-center mr-3">
        <div class="icon-circle mr-3" :class="interpretacion.iconCircleClass">
          <i :class="interpretacion.icon"></i>
        </div>
        <div>
          <div class="d-flex align-items-center mb-1">
            <span class="text-uppercase font-weight-bold text-muted mr-2" style="font-size: 0.68rem; letter-spacing: 0.5px;">
              Interpretación ({{ interpretacion.codigo }})
            </span>
            <span class="badge badge-pill font-weight-bold px-2" :class="interpretacion.badgeClass" style="font-size: 0.72rem;">
              {{ interpretacion.badgeText || (interpretacion.esPositivo ? 'Caso sospechoso' : 'Sin sospecha') }}
            </span>
          </div>
          <h6 v-if="interpretacion.titulo" class="mb-1 font-weight-bold" :class="interpretacion.textClass" style="font-size: 0.88rem;">
            {{ interpretacion.titulo }}
          </h6>
          <p class="mb-0 text-dark font-weight-semibold" style="font-size: 0.83rem; line-height: 1.35;">
            {{ interpretacion.mensaje }}
          </p>
        </div>
      </div>
      <div class="stat-box text-center pl-3 border-left flex-shrink-0">
        <span class="d-block text-muted font-weight-bold text-uppercase" style="font-size: 0.65rem;">
          {{ interpretacion.statLabel || 'Ítems (+)' }}
        </span>
        <span class="font-weight-bold" :class="interpretacion.textClass" style="font-size: 1.2rem; line-height: 1.1;">
          {{ interpretacion.totalPositivas }}<small class="text-muted font-weight-normal" style="font-size: 0.75rem;">/{{ interpretacion.totalPreguntas }}</small>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InterpretacionFormulario",
  props: {
    codigo: {
      type: String,
      default: "",
    },
    formulario: {
      type: [Array, Object],
      default: () => [],
    },
  },
  computed: {
    interpretacion() {
      if (!this.codigo || !this.formulario || !Array.isArray(this.formulario) || !this.formulario.length) {
        return null;
      }

      const cod = String(this.codigo).trim().toUpperCase();

      // Recolectar todas las preguntas del formulario en orden
      const todasLasPreguntas = [];
      this.formulario.forEach((sec) => {
        if (sec.preguntas && Array.isArray(sec.preguntas)) {
          todasLasPreguntas.push(...sec.preguntas);
        }
      });

      if (!todasLasPreguntas.length) return null;

      // Función auxiliar para verificar si una respuesta es afirmativa
      const esAfirmativo = (val) => {
        if (!val) return false;
        const str = String(val).trim().toUpperCase();
        return str === "SÍ" || str === "SI" || str === "1" || str === "TRUE" || str === "VERDADERO";
      };

      // 1. REGLA PARA RQC (Reporting Questionnaire for Children)
      if (cod === "RQC") {
        const totalSi = todasLasPreguntas.filter((p) => esAfirmativo(p.respuesta)).length;
        const tienePositivo = totalSi >= 1;

        return {
          tipo: "simple",
          codigo: "RQC",
          totalPositivas: totalSi,
          totalPreguntas: todasLasPreguntas.length,
          esPositivo: tienePositivo,
          titulo: tienePositivo ? "Tamizaje Positivo (Caso sospechoso)" : "Tamizaje Negativo (Sin sospecha)",
          mensaje: tienePositivo
            ? "Un ítem positivo indica que el NIÑO/A requiere evaluación integral, diagnóstico, tratamiento y seguimiento."
            : "Sin alertas identificadas en el cuestionario de salud mental.",
          cardClass: tienePositivo ? "border-left-danger" : "border-left-success",
          iconCircleClass: tienePositivo ? "icon-circle-danger" : "icon-circle-success",
          badgeClass: tienePositivo ? "badge-danger" : "badge-success",
          textClass: tienePositivo ? "text-danger" : "text-success",
          icon: tienePositivo ? "fas fa-exclamation-triangle" : "fas fa-check-circle",
        };
      }

      // 2. REGLA PARA SRQ (Self-Reporting Questionnaire - 30 preguntas)
      if (cod === "SRQ") {
        const q1_20 = todasLasPreguntas.slice(0, 20);
        const q21_24 = todasLasPreguntas.slice(20, 24);
        const q25 = todasLasPreguntas.length > 24 ? todasLasPreguntas[24] : null;
        const q26_30 = todasLasPreguntas.slice(25, 30);

        const si1_20 = q1_20.filter((p) => esAfirmativo(p.respuesta)).length;
        const si21_24 = q21_24.filter((p) => esAfirmativo(p.respuesta)).length;
        const si25 = q25 ? esAfirmativo(q25.respuesta) : false;
        const si26_30 = q26_30.filter((p) => esAfirmativo(p.respuesta)).length;

        // Criterios clínicos SRQ-30
        const alertaSaludMental = si1_20 >= 11;
        const alertaPsicosis = si21_24 >= 1;
        const alertaConvulsivo = si25;
        const alertaAlcohol = si26_30 >= 1;

        const tieneAlertaGlobal = alertaSaludMental || alertaPsicosis || alertaConvulsivo || alertaAlcohol;

        return {
          tipo: "srq",
          codigo: "SRQ",
          esPositivo: tieneAlertaGlobal,
          titulo: tieneAlertaGlobal
            ? "Caso sospechoso: requiere valoración especializada"
            : "Sin sospecha de riesgo identificada",
          badgeClass: tieneAlertaGlobal ? "badge-danger" : "badge-success",
          textClass: tieneAlertaGlobal ? "text-danger" : "text-success",
          icon: tieneAlertaGlobal ? "fas fa-exclamation-triangle" : "fas fa-check-circle",
          detalles: [
            {
              nombre: "Salud Mental Común (Preguntas 1 a 20)",
              subtitulo: "Depresión, ansiedad y síntomas no psicóticos",
              puntaje: `${si1_20} / 20`,
              alerta: alertaSaludMental,
              interpretacion: alertaSaludMental
                ? "Puntuación ≥ 11: Sugiere alta probabilidad de sufrir problemas de salud mental (ansiedad/depresión)."
                : "Puntuación < 11: Sin sospecha de problema de salud mental común.",
            },
            {
              nombre: "Posible Psicosis (Preguntas 21 a 24)",
              subtitulo: "Evaluación de síntomas psicóticos",
              puntaje: `${si21_24} / 4`,
              alerta: alertaPsicosis,
              interpretacion: alertaPsicosis
                ? "Con una respuesta positiva se considera un posible caso que requiere evaluación por psiquiatría."
                : "Sin sospecha de posible psicosis.",
            },
            {
              nombre: "Trastorno Convulsivo (Pregunta 25)",
              subtitulo: "Evaluación de crisis convulsivas",
              puntaje: si25 ? "Sí" : "No",
              alerta: alertaConvulsivo,
              interpretacion: alertaConvulsivo
                ? "La respuesta positiva a la pregunta 25 indica alta probabilidad de sufrir un trastorno convulsivo."
                : "Sin sospecha de trastorno convulsivo.",
            },
            {
              nombre: "Riesgo con el Alcohol (Preguntas 26 a 30)",
              subtitulo: "Problemas relacionados con consumo de alcohol",
              puntaje: `${si26_30} / 5`,
              alerta: alertaAlcohol,
              interpretacion: alertaAlcohol
                ? "La respuesta positiva a una sola de ellas determina que la persona tiene alto riesgo de sufrir alcoholismo."
                : "Sin riesgo identificado de alcoholismo.",
            },
          ],
        };
      }

      // 3. REGLA PARA PLUTCHIK (Escala de Riesgo Suicida de Plutchik - 15 ítems)
      if (cod === "PLUTCHIK") {
        const totalSi = todasLasPreguntas.filter((p) => esAfirmativo(p.respuesta)).length;
        const tieneRiesgo = totalSi >= 6;

        return {
          tipo: "simple",
          codigo: "PLUTCHIK",
          totalPositivas: totalSi,
          totalPreguntas: todasLasPreguntas.length || 15,
          esPositivo: tieneRiesgo,
          badgeText: tieneRiesgo ? "Riesgo Suicida (≥ 6)" : "Sin Riesgo (< 6)",
          statLabel: "Puntuación",
          titulo: tieneRiesgo
            ? "Riesgo de Conducta Suicida Identificado"
            : "Sin Riesgo Suicida Significativo",
          mensaje: tieneRiesgo
            ? "Puntuación igual o mayor a 6: A mayor puntuación, mayor riesgo. Los autores de la validación en español identifican como punto de corte una puntuación igual o mayor a 6 (indica riesgo de conducta suicida que amerita evaluación integral y seguimiento por salud mental)."
            : "Puntuación menor a 6: No supera el punto de corte (≥ 6) en la escala de riesgo suicida de Plutchik.",
          cardClass: tieneRiesgo ? "border-left-danger" : "border-left-success",
          iconCircleClass: tieneRiesgo ? "icon-circle-danger" : "icon-circle-success",
          badgeClass: tieneRiesgo ? "badge-danger" : "badge-success",
          textClass: tieneRiesgo ? "text-danger" : "text-success",
          icon: tieneRiesgo ? "fas fa-exclamation-triangle" : "fas fa-check-circle",
        };
      }

      return null;
    },
  },
};
</script>

<style scoped>
.interpretacion-wrapper {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;
}

.interpretacion-card {
  border-left: 4px solid transparent;
  background-color: #fff;
  transition: all 0.2s ease;
}

.border-left-danger {
  border-left-color: #e74a3b !important;
  border-top: 1px solid rgba(231, 74, 59, 0.2);
  border-right: 1px solid rgba(231, 74, 59, 0.2);
  border-bottom: 1px solid rgba(231, 74, 59, 0.2);
  background-color: #fff9f9 !important;
}

.border-left-success {
  border-left-color: #1cc88a !important;
  border-top: 1px solid rgba(28, 200, 138, 0.2);
  border-right: 1px solid rgba(28, 200, 138, 0.2);
  border-bottom: 1px solid rgba(28, 200, 138, 0.2);
  background-color: #f7fdfa !important;
}

.icon-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.icon-circle-danger {
  background-color: rgba(231, 74, 59, 0.12);
  color: #e74a3b;
}

.icon-circle-success {
  background-color: rgba(28, 200, 138, 0.12);
  color: #1cc88a;
}

.row-alerta {
  background-color: rgba(231, 74, 59, 0.06) !important;
}

.stat-box {
  min-width: 85px;
}

@media (max-width: 768px) {
  .interpretacion-wrapper {
    max-width: 100%;
  }
}
</style>
